/**
 * Терпимое приведение ответов API аналитики (волна 6 и далее): camelCase и snake_case, числа-строки MySQL,
 * пустое остаётся `null` (не ноль). Общие понятия контракта волны 6 — источники, «оценка», события ПИМ, объект
 * строки, ряды по дням — docs/SPEC_2026-10-20_ANALYTICS_WAVE6_API.md, раздел 0.
 */

import { toNumberOrNull } from './format'

export const asArray = <T = any>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : [])

export const textOrNull = (value: unknown): string | null =>
  value === null || value === undefined || String(value).trim() === '' ? null : String(value)

/** `snake_case` → `camelCase`: `orders_rub` → `ordersRub`. */
export const camelKey = (key: string): string => key.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase())
/** `camelCase` → `snake_case`. */
export const snakeKey = (key: string): string => key.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`)

/** Значение по первому найденному ключу; ключи проверяются и в camelCase, и в snake_case. */
export function pick(raw: any, ...keys: string[]): unknown {
  if (!raw || typeof raw !== 'object') return undefined
  for (const key of keys) {
    if (raw[key] !== undefined) return raw[key]
    const snake = snakeKey(key)
    if (raw[snake] !== undefined) return raw[snake]
  }
  return undefined
}

export const num = (raw: any, ...keys: string[]): number | null => toNumberOrNull(pick(raw, ...keys))
export const text = (raw: any, ...keys: string[]): string | null => textOrNull(pick(raw, ...keys))
export const bool = (value: unknown): boolean => value === true || value === 1 || value === '1' || value === 'true'
export const flag = (raw: any, ...keys: string[]): boolean => bool(pick(raw, ...keys))
export const dateOrNull = (value: unknown): string | null => textOrNull(value)?.slice(0, 10) ?? null
export const numbers = (value: unknown): Array<number | null> => asArray<unknown>(value).map(toNumberOrNull)
export const intOr = (value: unknown, fallback = 0): number => {
  const parsed = toNumberOrNull(value)
  return parsed === null ? fallback : Math.round(parsed)
}

/** Значение из перечня; иначе запасное. */
export function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  const textValue = String(value ?? '')
  return (allowed as readonly string[]).includes(textValue) ? (textValue as T) : fallback
}

export function marketplaceOf(value: unknown): 'wb' | 'ozon' | null {
  const textValue = String(value ?? '').toLowerCase()
  if (textValue.startsWith('w')) return 'wb'
  if (textValue.startsWith('o')) return 'ozon'
  return null
}

/**
 * Словарь «ключ → значение» из объекта ответа: ключи приводятся к camelCase, значения — к числу или `null`.
 * Отсутствующие ключи из `keys` заполняются `null` (нет данных), чтобы страница не проверяла `undefined`.
 */
export function numberRecord<K extends string>(raw: any, keys: readonly K[]): Record<K, number | null> {
  const result = {} as Record<K, number | null>
  for (const key of keys) result[key] = num(raw, key)
  return result
}

// ── Значение с «оценкой» (0.3) ──────────────────────────────────────────────

export interface MetricValue {
  value: number | null
  previous: number | null
  /** Оценка: период не закрыт, расчётная величина или прогноз. */
  estimate: boolean
}

export const EMPTY_METRIC: MetricValue = { value: null, previous: null, estimate: false }

/** Принимает `{ value, previous, estimate }` и голое число (тогда сравнения нет). */
export function normalizeMetric(raw: any): MetricValue {
  if (raw === null || raw === undefined) return { ...EMPTY_METRIC }
  if (typeof raw !== 'object') return { value: toNumberOrNull(raw), previous: null, estimate: false }
  return {
    value: num(raw, 'value', 'current'),
    previous: num(raw, 'previous', 'previousValue', 'prev'),
    estimate: flag(raw, 'estimate', 'isEstimate'),
  }
}

export function metricRecord<K extends string>(raw: any, keys: readonly K[]): Record<K, MetricValue> {
  const result = {} as Record<K, MetricValue>
  for (const key of keys) result[key] = normalizeMetric(pick(raw, key))
  return result
}

// ── Источники и доступность (0.4) ───────────────────────────────────────────

export type SourceStatus = 'ok' | 'stale' | 'partial' | 'not_connected' | 'error'
export const SOURCE_STATUSES: SourceStatus[] = ['ok', 'stale', 'partial', 'not_connected', 'error']

export interface SourceInfo {
  key: string
  label: string
  status: SourceStatus
  lastDate: string | null
  scope: string | null
  hint: string | null
}

export function normalizeSource(raw: any): SourceInfo {
  const key = text(raw, 'key', 'sourceKey') ?? 'unknown'
  return {
    key,
    label: text(raw, 'label', 'name') ?? key,
    status: oneOf(pick(raw, 'status'), SOURCE_STATUSES, 'ok'),
    lastDate: dateOrNull(pick(raw, 'lastDate', 'last')),
    scope: text(raw, 'scope'),
    hint: text(raw, 'hint', 'reason'),
  }
}

export const normalizeSources = (value: unknown): SourceInfo[] => asArray(value).map(normalizeSource)

export interface Availability {
  available: boolean
  reason: string | null
  sourceKey: string | null
}

/** Блок без поля `available` считается доступным: старый ответ не должен превращаться в «не подключено». */
export function normalizeAvailability(raw: any): Availability {
  const value = pick(raw, 'available')
  return {
    available: value === undefined || value === null ? true : bool(value),
    reason: text(raw, 'reason', 'hint'),
    sourceKey: text(raw, 'sourceKey', 'source'),
  }
}

// ── Источник расхода на рекламу (attribution) ───────────────────────────────

/**
 * Откуда расход на рекламу (бэкенд — management/ad-spend.ts; одно определение у «Рекламы», «Сводки» и РНП):
 * `direct` — свой загрузчик WB по SKU, `proportional` — списания кампаний с долей среза по заказам ₽ за день,
 * `mixed` — в периоде дни того и другого.
 */
export type AdAttribution = 'direct' | 'proportional' | 'mixed'
export const AD_ATTRIBUTIONS: AdAttribution[] = ['direct', 'proportional', 'mixed']

export const AD_ATTRIBUTION_LABELS: Record<AdAttribution, string> = {
  direct: 'Свой загрузчик рекламы WB по SKU',
  proportional: 'Списания кампаний, на срез — по доле заказов ₽ за день',
  mixed: 'Смешанно: часть дней — свой загрузчик, часть — списания кампаний по доле заказов',
}

/** Значение attribution из ответа; нет или мусор — `null`. */
export function adAttributionOf(value: unknown): AdAttribution | null {
  return AD_ATTRIBUTIONS.includes(value as AdAttribution) ? (value as AdAttribution) : null
}

/** Подписи ключей источника (`sourceKey`) у метрик рекламы и денег. */
export const METRIC_SOURCE_LABELS: Record<string, string> = {
  wb_ads: 'Реклама WB по SKU (свой загрузчик)',
  wb_advert_finances: 'Списания рекламных кампаний WB',
  ozon_ads: 'Реклама OZON (Performance API)',
  wb_orders: 'Заказы WB',
  fin: 'Финотчёты площадок (P&L)',
}

export const metricSourceLabel = (key: string | null | undefined): string | null => (key ? (METRIC_SOURCE_LABELS[key] ?? key) : null)

// ── События ПИМ (0.5) ───────────────────────────────────────────────────────

export type PimEventType = 'price' | 'promo_start' | 'promo_end' | 'photo' | 'title' | 'supply' | 'hypothesis'
export const PIM_EVENT_TYPES: PimEventType[] = ['price', 'promo_start', 'promo_end', 'photo', 'title', 'supply', 'hypothesis']

export const PIM_EVENT_LABELS: Record<PimEventType, string> = {
  price: 'Смена цены',
  promo_start: 'Старт акции',
  promo_end: 'Конец акции',
  photo: 'Смена фото',
  title: 'Смена названия',
  supply: 'Поставка',
  hypothesis: 'Изменение по гипотезе',
}

export interface PimEvent {
  date: string
  type: PimEventType
  label: string
  article: string | null
  count: number
}

export function normalizeEvents(value: unknown): PimEvent[] {
  return asArray(value)
    .map((raw) => ({
      date: dateOrNull(pick(raw, 'date')) ?? '',
      type: oneOf(pick(raw, 'type'), PIM_EVENT_TYPES, 'price'),
      label: text(raw, 'label') ?? '',
      article: text(raw, 'article'),
      count: intOr(pick(raw, 'count'), 1),
    }))
    .filter((event) => event.date !== '')
}

// ── Объект строки (0.7) ─────────────────────────────────────────────────────

export interface ObjectRef {
  dimension: string
  /** `null` — «Не сопоставлено». */
  key: string | null
  label: string
  sublabel: string | null
  imageUrl: string | null
  marketplace: 'wb' | 'ozon' | null
  sellerId: number | null
}

export function normalizeObject(raw: any, fallbackDimension = 'article'): ObjectRef {
  const source = raw && typeof raw === 'object' ? raw : {}
  const key = text(source, 'key', 'article', 'id')
  return {
    dimension: text(source, 'dimension') ?? fallbackDimension,
    key,
    label: text(source, 'label', 'name') ?? key ?? 'Не сопоставлено',
    sublabel: text(source, 'sublabel'),
    imageUrl: text(source, 'imageUrl', 'image'),
    marketplace: marketplaceOf(pick(source, 'marketplace')),
    sellerId: num(source, 'sellerId'),
  }
}

// ── Ряды по дням (0.6) ──────────────────────────────────────────────────────

export interface DailySeries<K extends string> {
  granularity: 'day' | 'week'
  dates: string[]
  values: Record<K, Array<number | null>>
  previous: Record<K, Array<number | null>> | null
}

/** Ряд выравнивается по длине `dates`: недостающие точки — `null`, лишние отбрасываются. */
export function normalizeDailySeries<K extends string>(raw: any, keys: readonly K[]): DailySeries<K> {
  const dates = asArray(pick(raw, 'dates')).map((value) => dateOrNull(value) ?? String(value))
  const fit = (values: unknown) => {
    const list = numbers(values)
    return dates.map((_date, index) => list[index] ?? null)
  }
  const valuesRaw = pick(raw, 'values') ?? {}
  const previousRaw = pick(raw, 'previous')
  const values = {} as Record<K, Array<number | null>>
  for (const key of keys) values[key] = fit(pick(valuesRaw, key))
  let previous: Record<K, Array<number | null>> | null = null
  if (previousRaw && typeof previousRaw === 'object') {
    previous = {} as Record<K, Array<number | null>>
    for (const key of keys) previous[key] = fit(pick(previousRaw, key))
  }
  return {
    granularity: pick(raw, 'granularity') === 'week' ? 'week' : 'day',
    dates,
    values,
    previous,
  }
}

export interface PersonRef {
  id: number
  name: string
}

export function normalizePerson(raw: any): PersonRef | null {
  if (!raw || typeof raw !== 'object') return null
  const id = num(raw, 'id', 'userId')
  if (id === null) return null
  return { id, name: text(raw, 'name', 'fullName', 'email') ?? `Пользователь ${id}` }
}
